/**
 * The language switcher.
 *
 * Sits beside the theme toggle in the header, because both are the same kind of
 * setting: a preference about how the app looks, not something about the data.
 *
 * The current language is shown as a two-letter code rather than a globe icon
 * alone — a globe tells you there are languages, the code tells you which one you
 * are already in, which is the question someone opening this menu actually has.
 */
import { LanguagesIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGES } from "@/i18n";

export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const active = i18n.resolvedLanguage ?? LANGUAGES[0].code;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("common.changeLanguage")}
          className="relative"
        >
          <LanguagesIcon />
          <span
            aria-hidden
            className="bg-background text-muted-foreground absolute -right-0.5 -bottom-0.5 rounded-sm px-0.5 text-[9px] leading-tight font-bold uppercase"
          >
            {active}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* A radio group, so a screen reader says which language is the current one;
            its item draws the check mark. */}
        <DropdownMenuRadioGroup value={active} onValueChange={(code) => void i18n.changeLanguage(code)}>
          {LANGUAGES.map((language) => (
            // `changeLanguage` writes the choice to localStorage through the
            // detector's cache, so nothing else has to remember it.
            <DropdownMenuRadioItem
              key={language.code}
              value={language.code}
              lang={language.code}
              data-active={active === language.code || undefined}
              className="data-[active]:bg-accent gap-2"
            >
              <span className="flex-1">{language.nativeName}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
